import type {PhaChange} from './analysis-changes.builder';
import {orderIdentity} from './pha-order-identity';

export interface PhaRecognizedOrder { name: string; changes: PhaChange[]; }

/** Only ungrouped pending changes enter recognition. Quantities are consumed exactly once. */
export function recognizeOptionOrders(changes: PhaChange[]): PhaRecognizedOrder[] {
    const remaining = changes.map(c => ({...c})).sort((a, b) =>
        a.expiration.localeCompare(b.expiration) || a.optionType.localeCompare(b.optionType)
        || a.strike - b.strike || a.rowKey.localeCompare(b.rowKey));
    const orders: PhaRecognizedOrder[] = [];
    const patterns = [
        {name: 'Condor', size: 4, ratios: [1, 1, 1, 1]},
        {name: 'Iron Condor', size: 4, ratios: [1, 1, 1, 1]},
        {name: 'Butterfly', size: 3, ratios: [1, 2, 1]},
        {name: 'Vertical', size: 2, ratios: [1, 1]}
    ];
    for (const pattern of patterns) {
        let match: PhaChange[];
        while ((match = findMatch(remaining.filter(c => c.qty > 0), pattern.name, pattern.size))) {
            const quantity = Math.min(...match.map((c, i) => Math.floor(c.qty / pattern.ratios[i])));
            if (!quantity) { break; }
            const identity = pattern.name + ':' + match.map(c => c.rowKey + ':' + (c.allocationOffset || 0)).join(';');
            const legs = match.map((c, i) => {
                const qty = quantity * pattern.ratios[i];
                const afterQty = c.beforeQty + c.side * qty;
                const leg = {...c, qty, afterQty, afterText: signedText(afterQty),
                    beforeText: signedText(c.beforeQty), rowKey: c.rowKey + '|allocation:' + orderIdentity(identity)};
                c.qty -= qty;
                c.beforeQty = afterQty;
                c.beforeText = signedText(afterQty);
                c.allocationOffset = (c.allocationOffset || 0) + qty;
                return leg;
            });
            orders.push({name: pattern.name, changes: legs});
        }
    }
    const residual = remaining.filter(c => c.qty > 0).map(c => ({...c, rowKey: c.rowKey + '|allocation:Residual:' + (c.allocationOffset || 0)}));
    for (let i = 0; i < residual.length; i += 4) {
        orders.push({name: 'Combo', changes: residual.slice(i, i + 4)});
    }
    return orders;
}

function signedText(qty: number): string { return qty > 0 ? '+' + qty : String(qty); }

function findMatch(legs: PhaChange[], name: string, size: number): PhaChange[] | null {
    if (name === 'Iron Condor' && (legs.filter(c => c.optionType === 'Put').length < 2
        || legs.filter(c => c.optionType === 'Call').length < 2)) { return null; }
    const visit = (start: number, selected: PhaChange[]): PhaChange[] | null => {
        if (selected.length === size) { return matches(selected, name) ? selected : null; }
        for (let i = start; i <= legs.length - (size - selected.length); i++) {
            const leg = legs[i];
            if (selected.length && (leg.underlying !== selected[0].underlying || leg.expiration !== selected[0].expiration)) { continue; }
            if (selected.length && leg.tradingInstrumentCode.split(' ')[0] !== selected[0].tradingInstrumentCode.split(' ')[0]) { continue; }
            if (name === 'Iron Condor') {
                const wing = selected.filter(c => c.optionType === leg.optionType);
                if (wing.length >= 2 || wing.length && (leg.strike <= wing[0].strike || leg.side === wing[0].side)) { continue; }
            }
            if (name !== 'Iron Condor' && selected.length) {
                if (leg.optionType !== selected[0].optionType || leg.strike <= selected[selected.length - 1].strike) { continue; }
                const expected = name === 'Condor' ? [1, -1, -1, 1] : name === 'Butterfly' ? [1, -1, 1] : [1, -1];
                if (leg.side !== selected[0].side * expected[selected.length]) { continue; }
                if (name === 'Butterfly' && selected.length === 1 && leg.qty < 2) { continue; }
            }
            const result = visit(i + 1, [...selected, leg]);
            if (result) { return result; }
        }
        return null;
    };
    return visit(0, []);
}

function matches(legs: PhaChange[], name: string): boolean {
    if (legs.some(c => c.strike == null || !c.expiration || c.qty <= 0)) { return false; }
    if (name === 'Iron Condor') {
        const puts = legs.filter(c => c.optionType === 'Put').sort((a, b) => a.strike - b.strike);
        const calls = legs.filter(c => c.optionType === 'Call').sort((a, b) => a.strike - b.strike);
        return puts.length === 2 && calls.length === 2 && puts[0].strike < puts[1].strike
            && puts[1].strike < calls[0].strike && calls[0].strike < calls[1].strike
            && puts[0].side === calls[1].side && puts[1].side === calls[0].side && puts[0].side !== puts[1].side;
    }
    if (legs.some((c, i) => i > 0 && c.strike <= legs[i - 1].strike)) { return false; }
    if (name === 'Vertical') { return legs[0].side !== legs[1].side; }
    if (name === 'Butterfly') {
        return legs[0].side === legs[2].side && legs[0].side !== legs[1].side && legs[1].qty >= 2;
    }
    return legs[0].side === legs[3].side && legs[1].side === legs[2].side && legs[0].side !== legs[1].side;
}
